import { Button, Spinner, Text, useToastContext } from "@ttc3k/trekker";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { css } from "styled-system/css";
import { Flex, styled } from "styled-system/jsx";
import { InlineSectionTitle } from "shared/components";
import { getClientUrl } from "shared/utils";
import { ShareLinkBox } from "../../../components/ShareLinkBox";
import { StickyHeader } from "../StickyHeader";
import { OrganizationSettingsViewKey } from "../../../useOrganizationSettings";
import { AnalysisSummary } from "./AnalysisSummary";
import { ChecklistFixDialog } from "./ChecklistFixDialog";
import { MarkStatusDialog } from "./MarkStatusDialog";
import { ChecklistItem } from "./ChecklistItem";
import { HowToTestCard } from "./HowToTestCard";
import {
  getLiveTargetFor,
  getPaymentState,
  isRenewalDateDriven,
  useMembershipChecklist,
  type ChecklistAnalysis,
  type ChecklistFixAction,
  type SetDatesOptions,
} from "./useMembershipChecklist";

const linkClassName = css({
  color: "text.accent.purple.mid",
  fontWeight: "medium",
  _hover: { color: "text.accent.purple.dark" },
  transitionProperty: "color",
  transitionDuration: "normal",
});

const formatMonthDay = (month: number, day: number) =>
  format(new Date(2024, month - 1, day), "MMMM d");

type ChecklistSectionProps = {
  organizationId?: string;
  membershipTranslationValues: Record<string, string>;
  onCreateMemberLists: () => Promise<void>;
  creatingMemberLists: boolean;
};

export const ChecklistSection = ({
  organizationId,
  membershipTranslationValues,
  onCreateMemberLists,
  creatingMemberLists,
}: ChecklistSectionProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.CHECKLIST",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    analyze,
    analyzing,
    loadingLatest,
    ranAt,
    analysis,
    statuses,
    previewFix,
    applyFix,
    copyPaymentToLive,
    copyingToLive,
  } = useMembershipChecklist(organizationId);

  const [openFix, setOpenFix] = useState<ChecklistFixAction | null>(null);
  const [markCohort, setMarkCohort] = useState<{
    fromTagId: string | null;
    fromName: string | null;
    count: number;
  } | null>(null);
  const tv = membershipTranslationValues;

  const goToStorefrontSettings = () => {
    const next = new URLSearchParams(searchParams);
    next.set("settingsSubView", OrganizationSettingsViewKey.StorefrontSettings);
    setSearchParams(next);
  };

  const handleAnalyze = async () => {
    try {
      await analyze();
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ANALYZE_ERROR"),
      });
    }
  };

  const handleCreateLists = async () => {
    await onCreateMemberLists();
    await analyze();
  };

  const handleApplyFix = async (
    action: ChecklistFixAction,
    date?: Date,
    setDates?: SetDatesOptions,
  ) => {
    try {
      const affected = await applyFix(
        action,
        date,
        undefined,
        undefined,
        setDates,
      );
      setOpenFix(null);
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t(`FIX.${action}.SUCCESS`, { ...tv, count: affected }),
      });
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("FIX.ERROR"),
      });
    }
  };

  const handleCopyToLive = async () => {
    try {
      const copied = await copyPaymentToLive();
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("PAYMENT.COPY_SUCCESS", { count: copied }),
      });
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("PAYMENT.COPY_ERROR"),
      });
    }
  };

  const fixButton = (action: ChecklistFixAction, label: string) => (
    <Button
      key={action}
      visual="primary"
      mode="light"
      size="sm"
      type="button"
      onClick={() => setOpenFix(action)}
    >
      {label}
    </Button>
  );

  const renderTiers = (a: ChecklistAnalysis) => {
    const tiers = a.members.byTier;
    const missingList = tiers.some(tier => !tier.listId);
    return (
      <ChecklistItem
        status={statuses!.tiers}
        title={t("TIERS.TITLE")}
        action={
          a.memberListsEnabled && tiers.length > 0 && missingList ? (
            <Button
              visual="primary"
              mode="light"
              size="sm"
              type="button"
              onClick={handleCreateLists}
              isLoading={creatingMemberLists}
              disabled={creatingMemberLists}
            >
              {t("TIERS.CREATE_LISTS", tv)}
            </Button>
          ) : undefined
        }
      >
        {tiers.length === 0 ? (
          <Text size="sm" color="text.mid">
            {t("TIERS.NONE", tv)}
          </Text>
        ) : (
          <Text size="sm" color="text.mid">
            {tiers.map((tier, i) => (
              <span key={tier.tagId}>
                {i > 0 && " · "}
                {tier.listId ? (
                  <Link
                    to={`/app/organization/${organizationId}/custom-lists/${tier.listId}`}
                    className={linkClassName}
                  >
                    {`${tier.name} (${tier.count})`}
                  </Link>
                ) : (
                  `${tier.name} (${tier.count})`
                )}
              </span>
            ))}
          </Text>
        )}
        {!a.memberListsEnabled && tiers.length > 0 && (
          <Text size="sm" color="text.light">
            {t("TIERS.LISTS_NOT_ENABLED")}
          </Text>
        )}
      </ChecklistItem>
    );
  };

  const renderTierConflicts = (a: ChecklistAnalysis) => (
    <ChecklistItem
      status={statuses!.tierConflicts}
      title={t("TIER_CONFLICTS.TITLE")}
      action={
        a.ledgerEnabled && a.members.multipleTiers > 0
          ? fixButton(
              "RESOLVE_TIERS",
              t("TIER_CONFLICTS.ACTION", { count: a.members.multipleTiers }),
            )
          : undefined
      }
    >
      <Text size="sm" color="text.mid">
        {a.members.multipleTiers === 0
          ? t("TIER_CONFLICTS.DONE")
          : t("TIER_CONFLICTS.MISSING", { count: a.members.multipleTiers })}
      </Text>
      {a.members.multipleTierListings.length > 0 && (
        <styled.ul display="flex" flexDir="column" gap="50" listStyleType="none">
          {a.members.multipleTierListings.map(listing => (
            <Text as="li" key={listing.entityId} size="sm" color="text.mid">
              <Link
                to={`/app/organization/${organizationId}/directory/${listing.entityId}?tab=BACK_OFFICE`}
                className={linkClassName}
              >
                {listing.name}
              </Link>
              {` — ${listing.tiers.join(", ")}`}
            </Text>
          ))}
        </styled.ul>
      )}
    </ChecklistItem>
  );

  const renewalExplanation = (r: ChecklistAnalysis["renewal"]) => {
    const years = r.termYears ?? 2;
    if (r.renewalType === "FIXED_DATE") {
      if (!r.fixedRenewalMonth || !r.fixedRenewalDay) {
        return t("RENEWALS.FIXED_MISSING_DATE");
      }
      return t(
        r.term === "MULTI_YEAR" ? "RENEWALS.FIXED_MULTI" : "RENEWALS.FIXED",
        {
          ...tv,
          date: formatMonthDay(r.fixedRenewalMonth, r.fixedRenewalDay),
          years,
        },
      );
    }
    const termKey =
      r.term === "MONTHLY"
        ? "RENEWALS.ANNIVERSARY_MONTHLY"
        : r.term === "MULTI_YEAR"
          ? "RENEWALS.ANNIVERSARY_MULTI"
          : "RENEWALS.ANNIVERSARY_YEARLY";
    return t(termKey, { ...tv, years });
  };

  const renderRenewals = (a: ChecklistAnalysis) => {
    const r = a.renewal;
    if (statuses!.renewals === "notRelevant") {
      return (
        <ChecklistItem status="notRelevant" title={t("RENEWALS.TITLE")}>
          <Text size="sm" color="text.mid">
            {t(
              !r.engineEnabled
                ? "RENEWALS.NOT_ENABLED"
                : !r.enabled
                  ? "RENEWALS.OFF"
                  : "RENEWALS.NO_SCHEDULE",
              tv,
            )}
          </Text>
        </ChecklistItem>
      );
    }
    const canBulkSet =
      r.withoutRenewalDate > 0 &&
      (r.renewalType === "ANNIVERSARY" || !!r.suggestedBulkDate);
    return (
      <ChecklistItem
        status={statuses!.renewals}
        title={t("RENEWALS.TITLE")}
        action={
          canBulkSet
            ? fixButton(
                "SET_DATES",
                t("RENEWALS.BULK_BUTTON", { count: r.withoutRenewalDate }),
              )
            : undefined
        }
      >
        <Text size="sm" color="text.mid">
          {renewalExplanation(r)}
        </Text>
        <Text size="sm" color="text.mid">
          {r.withoutRenewalDate === 0
            ? t("RENEWALS.ALL_HAVE_DATE", tv)
            : t("RENEWALS.MISSING_DATE", { count: r.withoutRenewalDate })}
        </Text>
        {r.renewalType === "ANNIVERSARY" && r.withoutRenewalDate > 0 && (
          <Text size="sm" color="text.light">
            {t("RENEWALS.IMPORT_HINT")}
          </Text>
        )}
      </ChecklistItem>
    );
  };

  const handleMarkStatus = async (statusTagId: string) => {
    if (!analysis) return;
    const statusName =
      analysis.members.byStatus.find(s => s.tagId === statusTagId)?.name ?? "";
    try {
      const affected = await applyFix(
        "MARK_ACTIVE",
        undefined,
        statusTagId,
        markCohort?.fromTagId,
      );
      setMarkCohort(null);
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("STATUS_TAGS.MARK_SUCCESS", {
          ...tv,
          count: affected,
          status: statusName,
        }),
      });
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("FIX.ERROR"),
      });
    }
  };

  const renderStatusTags = (a: ChecklistAnalysis) => {
    const { tieredWithoutStatusTag, byStatus } = a.members;
    const { tagDrift } = a.renewal;
    if (statuses!.statusTags === "notRelevant") {
      return (
        <ChecklistItem status="notRelevant" title={t("STATUS_TAGS.TITLE")}>
          <Text size="sm" color="text.mid">
            {t("STATUS_TAGS.NONE_CONFIGURED", tv)}
          </Text>
        </ChecklistItem>
      );
    }
    const bulkEdit = (
      cohort: {
        fromTagId: string | null;
        fromName: string | null;
        count: number;
      },
    ) =>
      a.ledgerEnabled && cohort.count > 0 && byStatus.length > 0 ? (
        <Button
          visual="primary"
          mode="light"
          size="sm"
          type="button"
          flexShrink={0}
          onClick={() => setMarkCohort(cohort)}
        >
          {t("STATUS_TAGS.BULK_EDIT")}
        </Button>
      ) : null;
    const actions = [
      tagDrift > 0 && a.ledgerEnabled
        ? fixButton(
            "FIX_STATUS",
            t("STATUS_TAGS.FIX_DRIFT", { count: tagDrift }),
          )
        : null,
    ].filter(Boolean);
    return (
      <ChecklistItem
        status={statuses!.statusTags}
        title={t("STATUS_TAGS.TITLE")}
        action={actions.length > 0 ? <>{actions}</> : undefined}
      >
        <Flex flexDir="column" gap="100">
          {byStatus.map(status => (
            <Flex
              key={status.tagId}
              alignItems="center"
              justifyContent="space-between"
              gap="200"
            >
              <Text size="sm" color="text.mid">
                <Text as="span" visual="smallSemiBold" color="text.dark">
                  {status.name}
                </Text>
                {`: ${t("STATUS_TAGS.COUNT", { count: status.count })}`}
              </Text>
              {bulkEdit({
                fromTagId: status.tagId,
                fromName: status.name,
                count: status.count,
              })}
            </Flex>
          ))}
        </Flex>
        {tieredWithoutStatusTag === 0 && tagDrift === 0 && (
          <Text size="sm" color="text.mid">
            {t("STATUS_TAGS.DONE")}
          </Text>
        )}
        {tieredWithoutStatusTag > 0 && (
          <Flex alignItems="center" justifyContent="space-between" gap="200">
            <Text size="sm" color="text.mid">
              {t("STATUS_TAGS.MISSING", { count: tieredWithoutStatusTag })}
            </Text>
            {bulkEdit({
              fromTagId: null,
              fromName: null,
              count: tieredWithoutStatusTag,
            })}
          </Flex>
        )}
        {tieredWithoutStatusTag > 0 && byStatus.length === 0 && (
          <Text size="sm" color="text.light">
            {t("STATUS_TAGS.NO_ACTIVE_TAG")}
          </Text>
        )}
        {tagDrift > 0 && (
          <Text size="sm" color="text.mid">
            {t("STATUS_TAGS.DRIFT", { count: tagDrift })}
          </Text>
        )}
      </ChecklistItem>
    );
  };

  const renderPayment = (a: ChecklistAnalysis) => {
    const state = getPaymentState(a);
    const tierName = new Map(a.members.byTier.map(tier => [tier.tagId, tier.name]));
    const unconfigured = a.payment.byTier
      .filter(tier => !tier.free && !tier.bookerId)
      .map(tier => tierName.get(tier.tagId) ?? tier.tagId);
    const testTiers = a.payment.byTier.filter(
      tier => tier.isTest && tier.bookerStorefrontId,
    );
    const copyable = testTiers.filter(
      tier => !!getLiveTargetFor(a, tier.bookerStorefrontId!),
    );
    const missingLive = testTiers.length > copyable.length;

    return (
      <ChecklistItem
        status={statuses!.payment}
        title={t("PAYMENT.TITLE")}
        action={
          state === "testOnly" && copyable.length > 0 ? (
            <Button
              visual="primary"
              mode="light"
              size="sm"
              type="button"
              onClick={handleCopyToLive}
              isLoading={copyingToLive}
              disabled={copyingToLive}
            >
              {t("PAYMENT.COPY_TO_LIVE")}
            </Button>
          ) : state === "noStorefront" || (state === "testOnly" && missingLive) ? (
            <Button
              visual="primary"
              mode="light"
              size="sm"
              type="button"
              onClick={goToStorefrontSettings}
            >
              {t("PAYMENT.STOREFRONT_SETTINGS")}
            </Button>
          ) : undefined
        }
      >
        <Text size="sm" color="text.mid">
          {state === "disabled"
            ? t("PAYMENT.DISABLED", tv)
            : state === "noStorefront"
              ? t("PAYMENT.NO_STOREFRONT", tv)
              : state === "incomplete"
                ? t("PAYMENT.INCOMPLETE", { tiers: unconfigured.join(", ") })
                : state === "testOnly"
                  ? t("PAYMENT.TEST_ONLY", tv)
                  : t("PAYMENT.LIVE", tv)}
        </Text>
        {state === "testOnly" && copyable.length > 0 && (
          <Text size="sm" color="text.light">
            {t("PAYMENT.COPY_NOTE")}
          </Text>
        )}
        {state === "testOnly" && missingLive && (
          <Text size="sm" color="text.light">
            {t("PAYMENT.NO_LIVE_STOREFRONT")}
          </Text>
        )}
      </ChecklistItem>
    );
  };

  const renderCommunication = (a: ChecklistAnalysis) => {
    const c = a.communication;
    const shareUrl = c.broadcastId
      ? `${getClientUrl()}/?organizationId=${organizationId}&broadcast=${c.broadcastId}`
      : null;
    return (
      <ChecklistItem
        status={statuses!.communication}
        title={t("COMMUNICATION.TITLE")}
      >
        <Text size="sm" color="text.mid">
          {!c.broadcastId
            ? t("COMMUNICATION.NONE", tv)
            : !c.broadcastPublished
              ? t("COMMUNICATION.UNPUBLISHED", { name: c.broadcastName ?? "" })
              : t("COMMUNICATION.SELECTED", {
                  ...tv,
                  name: c.broadcastName ?? "",
                })}
        </Text>
        {c.broadcastId && c.broadcastPublished && c.broadcastSharedToAll === false && (
          <Text size="sm" color="text.error">
            {t("COMMUNICATION.NOT_SHARED_TO_ALL", tv)}
          </Text>
        )}
        {shareUrl && c.broadcastPublished && (
          <>
            <Text visual="smallMedium" color="text.mid">
              {t("COMMUNICATION.SHARE_LABEL")}
            </Text>
            <ShareLinkBox url={shareUrl} />
            {!c.allowColdSignup && (
              <Text size="sm" color="text.light">
                {t("COMMUNICATION.NO_COLD_SIGNUP")}
              </Text>
            )}
          </>
        )}
      </ChecklistItem>
    );
  };

  return (
    <Flex flexDir="column" gap="250" pt="300" w="full" alignSelf="flex-start">
      <StickyHeader>
        <InlineSectionTitle
          title={t("TITLE")}
          dividerProps={{ display: "none" }}
        />
      </StickyHeader>
      <Text visual="smallRegular" color="text.mid">
        {t("INTRO", tv)}
      </Text>
      <Flex gap="200" alignItems="center" flexWrap="wrap">
        <Button
          visual="primary"
          mode="light"
          type="button"
          onClick={handleAnalyze}
          isLoading={analyzing}
          disabled={analyzing || !organizationId}
        >
          {analysis ? t("REANALYZE") : t("ANALYZE")}
        </Button>
        {ranAt && (
          <Text size="sm" color="text.light">
            {t("LAST_RUN", {
              date: format(new Date(ranAt), "MMMM d, yyyy 'at' h:mm a"),
            })}
          </Text>
        )}
      </Flex>

      {loadingLatest && <Spinner size="sm" />}

      {analysis && statuses && (
        <>
          <AnalysisSummary
            analysis={analysis}
            membershipTranslationValues={tv}
          />
          <Flex flexDir="column">
            {renderTiers(analysis)}
            {renderTierConflicts(analysis)}
            {renderRenewals(analysis)}
            {renderStatusTags(analysis)}
            {renderPayment(analysis)}
            {renderCommunication(analysis)}
          </Flex>
          <HowToTestCard membershipTranslationValues={tv} />
        </>
      )}

      {markCohort && analysis && (
        <MarkStatusDialog
          count={markCohort.count}
          fromTagId={markCohort.fromTagId}
          fromName={markCohort.fromName}
          statuses={analysis.members.byStatus}
          attentionTagIds={analysis.renewal.attentionStatusTagIds ?? []}
          defaultStatusId={analysis.renewal.activeStatusTagId}
          onApply={handleMarkStatus}
          onClose={() => setMarkCohort(null)}
          membershipTranslationValues={tv}
        />
      )}

      {openFix && analysis && (
        <ChecklistFixDialog
          action={openFix}
          needsDate={
            openFix === "SET_DATES" &&
            isRenewalDateDriven(analysis.renewal) &&
            analysis.renewal.renewalType === "ANNIVERSARY"
          }
          cycleEndDate={
            openFix === "SET_DATES" &&
            analysis.renewal.renewalType === "FIXED_DATE"
              ? analysis.renewal.suggestedBulkDate
              : null
          }
          onPreview={(date, setDates) => previewFix(openFix, date, setDates)}
          onApply={(date, setDates) => handleApplyFix(openFix, date, setDates)}
          onClose={() => setOpenFix(null)}
          membershipTranslationValues={tv}
        />
      )}
    </Flex>
  );
};
